import classnames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { ChevronDirection, ChevronIcon, ErrorIcon, InfoIcon } from '../Icon';
import { RequiredStateText } from '../RequiredStateText';
import Tooltip from '../Tooltip';
import { normalizeTestId } from '../../utils';
import './Dropdown.css';

export interface DropdownOption<T = string> {
  label: string;
  value: T;
}

export interface DropdownProps<T = string> {
  /**
   * Options to display in the dropdown
   */
  options: DropdownOption<T>[];
  /**
   * Label for the dropdown
   */
  label?: string;
  /**
   * Name attribute for the dropdown
   */
  name: string;
  /**
   * Default value for the dropdown (uncontrolled mode)
   */
  defaultValue?: T;
  /**
   * Controlled value for the dropdown
   */
  value?: T;
  /**
   * Callback when dropdown value changes
   */
  onChange?: (value: T) => void;
  /**
   * Callback when dropdown loses focus
   */
  onBlur?: () => void;
  /**
   * Additional class name
   */
  className?: string;
  /**
   * Whether the dropdown is disabled
   */
  disabled?: boolean;
  /**
   * Hint text to display below the label
   */
  hint?: string;
  /**
   * Error message to display
   */
  errorMessage?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Tooltip info text to display next to the label
   */
  tooltipText?: string;
  /**
   * Enable search/filter functionality
   */
  searchable?: boolean;
  /**
   * Autocomplete attribute for browser autofill
   */
  autoComplete?: string;
  /**
   * Text shown before an option is selected
   */
  placeholder?: string;
  /** Optional visible renderer for each option, including the selected value */
  renderOption?: (option: DropdownOption<T>) => ReactNode;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function isTypeaheadKey(key: string): boolean {
  return key.length === 1 && /^[a-zA-Z0-9]$/.test(key);
}

function Dropdown<T = string>({
  options,
  label,
  name,
  defaultValue,
  value: controlledValue,
  onChange,
  onBlur,
  className,
  disabled,
  hint,
  errorMessage,
  required,
  tooltipText,
  searchable = false,
  autoComplete,
  placeholder = 'Select an option',
  renderOption,
  'data-testid': dataTestId,
}: DropdownProps<T>) {
  const hasError = !!errorMessage;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalValue, setInternalValue] = useState<T | undefined>(controlledValue ?? defaultValue);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const isControlled = controlledValue !== undefined;
  const selectedValue = isControlled ? controlledValue : internalValue;

  useEffect(() => {
    if (isControlled) {
      setInternalValue(controlledValue);
    }
  }, [isControlled, controlledValue]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pendingHighlightIndexRef = useRef<number | null>(null);
  const prevIsOpenRef = useRef<boolean>(false);

  const dropdownClasses = classnames('access-dropdown', className, {
    'access-dropdown--error': hasError,
    'access-dropdown--searchable': searchable,
  });
  const baseTestId = dataTestId ?? 'dropdown';

  const errorId = `${name}-error`;
  const tooltipId = `${name}-tooltip`;
  const labelId = `${name}-label`;
  const labelTextId = `${name}-label-text`;
  const requiredTextId = `${name}-required`;
  const valueId = `${name}-value`;
  const listboxId = `${name}-listbox`;
  const comboboxId = `${name}-button`;
  // Reference the label text only. `aria-labelledby` resolves to the full subtree
  // of whatever it points at, so referencing the whole <label> leaked the required
  // asterisk and the tooltip trigger's aria-label into the announced name.
  //
  // "required" is named explicitly because JAWS does not speak `aria-required` — see
  // RequiredStateText. It is its own span, so the tooltip noise stays out.
  const labelledBy = label
    ? [labelTextId, required ? requiredTextId : null, valueId].filter(Boolean).join(' ')
    : undefined;

  const filteredOptions =
    searchable && searchTerm
      ? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
      : options;

  const getOptionId = (index: number) => `${name}-option-${index}`;
  const activeOptionId = isOpen && highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined;

  const getInitialHighlightIndex = (optionList: DropdownOption<T>[]) => {
    if (optionList.length === 0) {
      return -1;
    }

    const currentIndex = optionList.findIndex((opt) => opt.value === selectedValue);

    return currentIndex >= 0 ? currentIndex : 0;
  };

  const findTypeaheadIndex = (key: string, optionList: DropdownOption<T>[]) => {
    const pressedKey = key.toLowerCase();
    const matchingOptions = optionList
      .map((option, index) => ({ index, option }))
      .filter(({ option }) => option.label.toLowerCase().startsWith(pressedKey))
      .sort((a, b) => a.option.label.localeCompare(b.option.label));

    return matchingOptions[0]?.index ?? -1;
  };

  const openList = (highlightIndex?: number) => {
    const nextIndex = highlightIndex ?? getInitialHighlightIndex(filteredOptions);
    pendingHighlightIndexRef.current = nextIndex;
    setIsOpen(true);
  };

  const closeList = (restoreFocus = false) => {
    setIsOpen(false);
    if (restoreFocus) {
      comboboxRef.current?.focus();
    }
  };

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    // Trigger onBlur when dropdown closes (transitions from open to closed)
    if (prevIsOpenRef.current && !isOpen && onBlur) {
      onBlur();
    }

    if (!isOpen) {
      setHighlightedIndex(-1);
      pendingHighlightIndexRef.current = null;
    } else if (pendingHighlightIndexRef.current !== null) {
      // Apply pending highlight index after dropdown opens
      setHighlightedIndex(pendingHighlightIndexRef.current);
      pendingHighlightIndexRef.current = null;
    }

    // Update previous open state
    prevIsOpenRef.current = isOpen;
  }, [isOpen, onBlur]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) {
      return;
    }

    optionRefs.current[highlightedIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedIndex, isOpen]);

  const handleOptionClick = (value: T) => {
    if (!isControlled) {
      setInternalValue(value);
    }
    setIsOpen(false);

    if (onChange) {
      onChange(value);
    }

    comboboxRef.current?.focus();
  };

  const selectHighlighted = () => {
    if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
      handleOptionClick(filteredOptions[highlightedIndex].value);
    }
  };

  const moveHighlight = (nextIndex: number) => {
    if (filteredOptions.length === 0) {
      return;
    }

    const lastIndex = filteredOptions.length - 1;
    const clampedIndex = ((nextIndex % filteredOptions.length) + filteredOptions.length) % filteredOptions.length;
    const boundedIndex = Math.max(0, Math.min(clampedIndex, lastIndex));
    setHighlightedIndex(boundedIndex);
  };

  const handleOpenListKeys = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      openList();

      return true;
    }

    if (!searchable && isTypeaheadKey(event.key)) {
      event.preventDefault();
      const matchingIndex = findTypeaheadIndex(event.key, filteredOptions);
      if (matchingIndex >= 0) {
        openList(matchingIndex);
      }

      return true;
    }

    return false;
  };

  const handleListNavigationKeys = (
    event: React.KeyboardEvent,
    spaceSelects: boolean,
    restoreFocusOnEscape: boolean,
  ) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveHighlight(highlightedIndex < 0 ? 0 : highlightedIndex + 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        moveHighlight(highlightedIndex < 0 ? filteredOptions.length - 1 : highlightedIndex - 1);
        break;

      case 'Enter':
        event.preventDefault();
        selectHighlighted();
        break;

      case ' ':
        if (!spaceSelects) {
          return;
        }
        event.preventDefault();
        selectHighlighted();
        break;

      case 'Escape':
        event.preventDefault();
        closeList(restoreFocusOnEscape);
        break;

      case 'Tab':
        closeList();
        break;

      case 'Home':
        event.preventDefault();
        moveHighlight(0);
        break;

      case 'End':
        event.preventDefault();
        moveHighlight(filteredOptions.length - 1);
        break;

      default:
        if (spaceSelects && isTypeaheadKey(event.key)) {
          event.preventDefault();
          const matchingIndex = findTypeaheadIndex(event.key, filteredOptions);
          if (matchingIndex >= 0) {
            setHighlightedIndex(matchingIndex);
          }
        }
        break;
    }
  };

  const handleComboboxKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    if (!isOpen) {
      handleOpenListKeys(event);

      return;
    }

    handleListNavigationKeys(event, true, false);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }

    handleListNavigationKeys(event, false, true);
  };

  const handleComboboxClick = () => {
    if (disabled) {
      return;
    }

    if (isOpen) {
      setIsOpen(false);
    } else {
      openList();
    }
  };

  const handleLabelClick = (event: React.MouseEvent<HTMLLabelElement>) => {
    if (disabled) {
      return;
    }

    const clickTarget = event.target;
    if (clickTarget instanceof Element && clickTarget.closest('button')) {
      return;
    }

    comboboxRef.current?.focus();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setHighlightedIndex(0);
  };

  const selectedOption = options.find((opt) => opt.value === selectedValue);
  const displayText = selectedOption?.label || placeholder;

  const valueToString = (value: T | undefined): string => {
    if (value === undefined || value === null) return '';

    return String(value);
  };

  return (
    <div
      ref={dropdownRef}
      className={dropdownClasses}
      data-testid={baseTestId}>
      <div
        className='access-dropdown__label-wrapper'
        data-testid={`${baseTestId}-label`}>
        {label && (
          <label
            className='ds-c-label'
            id={labelId}
            onClick={handleLabelClick}>
            <span className='access-dropdown__label-content'>
              <span id={labelTextId}>{label}</span>
              {(required || tooltipText) && (
                <span className='access-dropdown__label-suffix'>
                  {required && (
                    <>
                      <span
                        aria-hidden='true'
                        className='access-dropdown__required-asterisk'>
                        *
                      </span>
                      <RequiredStateText id={requiredTextId} />
                    </>
                  )}
                  {tooltipText && (
                    <Tooltip
                      className='access-dropdown__info-tooltip'
                      id={tooltipId}
                      placement='top'
                      title={tooltipText}>
                      <InfoIcon />
                    </Tooltip>
                  )}
                </span>
              )}
            </span>
          </label>
        )}
      </div>
      <div
        className='access-dropdown__field-wrapper'
        data-testid={`${baseTestId}-field`}>
        <div
          className='access-dropdown__custom-wrapper'
          data-testid={`${baseTestId}-custom`}>
          <div
            ref={comboboxRef}
            aria-activedescendant={searchable ? undefined : activeOptionId}
            aria-autocomplete={searchable ? 'list' : 'none'}
            aria-controls={isOpen ? listboxId : undefined}
            aria-describedby={errorMessage ? errorId : undefined}
            aria-disabled={disabled || undefined}
            aria-expanded={isOpen}
            aria-haspopup='listbox'
            aria-invalid={hasError}
            aria-label={label ? undefined : displayText}
            aria-labelledby={labelledBy}
            aria-required={required}
            className='access-dropdown__custom-button'
            data-testid={`${baseTestId}-button`}
            id={comboboxId}
            role='combobox'
            tabIndex={disabled || (searchable && isOpen) ? -1 : 0}
            onClick={handleComboboxClick}
            onKeyDown={handleComboboxKeyDown}>
            <span
              className='access-dropdown__custom-button-text'
              id={valueId}>
              {selectedOption && renderOption ? renderOption(selectedOption) : displayText}
            </span>
            <ChevronIcon direction={isOpen ? ChevronDirection.UP : ChevronDirection.DOWN} />
          </div>

          {isOpen && (
            <div
              aria-labelledby={label ? labelTextId : undefined}
              className='access-dropdown__custom-list'
              data-testid={`${baseTestId}-list`}
              id={listboxId}
              role='listbox'>
              {searchable && (
                <div
                  className='access-dropdown__search-wrapper'
                  data-testid={`${baseTestId}-search`}>
                  <input
                    ref={searchInputRef}
                    aria-activedescendant={activeOptionId}
                    aria-autocomplete='list'
                    aria-controls={listboxId}
                    aria-label='Search options'
                    className='access-dropdown__search-input'
                    data-testid={`${baseTestId}-search-input`}
                    placeholder='Search...'
                    type='text'
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
              )}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <div
                    key={`${String(option.value)}-${index}`}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    aria-selected={option.value === selectedValue}
                    className={classnames('access-dropdown__custom-option', {
                      'access-dropdown__custom-option--highlighted': highlightedIndex === index,
                      'access-dropdown__custom-option--selected': option.value === selectedValue,
                    })}
                    data-testid={`${baseTestId}-option-${normalizeTestId(valueToString(option.value)) || index}`}
                    id={getOptionId(index)}
                    role='option'
                    onClick={() => handleOptionClick(option.value)}>
                    {renderOption ? renderOption(option) : option.label}
                  </div>
                ))
              ) : (
                <div
                  className='access-dropdown__no-results'
                  data-testid={`${baseTestId}-no-results`}>
                  No results found
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className='access-dropdown__error-icon-wrapper'
          data-testid={`${baseTestId}-error`}>
          {errorMessage ? (
            <>
              <span
                className='sr-only'
                id={errorId}>
                {errorMessage}
              </span>
              <Tooltip
                isError
                activeClassName='access-dropdown__error-tooltip--active'
                className='access-dropdown__error-icon'
                placement='top'
                title={errorMessage}>
                <span
                  className='access-dropdown__error-icon-content'
                  role='img'>
                  <ErrorIcon ariaLabel={`Error: ${errorMessage}`} />
                </span>
              </Tooltip>
            </>
          ) : (
            <span
              aria-hidden='true'
              className='access-dropdown__error-icon-placeholder'
            />
          )}
        </div>
      </div>

      {/* Hidden select for form submission */}
      <select
        aria-hidden='true'
        autoComplete={autoComplete}
        name={name}
        style={{ display: 'none' }}
        tabIndex={-1}
        value={valueToString(selectedValue)}
        onChange={() => {}}>
        {options.map((option, index) => (
          <option
            key={`${String(option.value)}-${index}`}
            value={String(option.value)}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default Dropdown;
